import api from './api';

export interface Permission {
  id: number;
  name: string;
  resource: string;
  action: string;
  created_at?: string;
}

export interface Role {
  id: number;
  name: string;
  description?: string;
  permissions?: Permission[];
  created_at?: string;
}

export interface UserPermissions {
  permissions: string[]; // Array of "resource:action" strings
  menu_permissions: { [resource: string]: string[] }; // Resource -> actions mapping
  role?: Role;
}

export interface CreatePermissionData {
  name: string;
  resource: string;
  action: string;
}

export interface UpdatePermissionData {
  name?: string;
  resource?: string;
  action?: string;
}

export const permissionService = {
  getMyPermissions: async (): Promise<UserPermissions> => {
    const response = await api.get('/api/user/permissions');
    return response.data;
  },

  getAll: async (grouped?: boolean): Promise<Permission[] | { [resource: string]: Permission[] }> => {
    const params = grouped ? { grouped: 'true' } : {};
    const response = await api.get('/api/permissions', { params });
    return response.data.data || [];
  },

  getById: async (id: number): Promise<Permission> => {
    const response = await api.get(`/api/permissions/${id}`);
    return response.data.data;
  },

  create: async (data: CreatePermissionData): Promise<Permission> => {
    const response = await api.post('/api/permissions', data);
    return response.data.data;
  },

  update: async (id: number, data: UpdatePermissionData): Promise<Permission> => {
    const response = await api.put(`/api/permissions/${id}`, data);
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/permissions/${id}`);
  },

  hasPermission: (permissions: string[], resource: string, action: string): boolean => {
    const permissionString = `${resource}:${action}`;
    return permissions.includes(permissionString);
  },

  hasAnyPermission: (permissions: string[], checks: Array<{ resource: string; action: string }>): boolean => {
    return checks.some(check => permissionService.hasPermission(permissions, check.resource, check.action));
  },

  canViewMenu: (menuPermissions: { [resource: string]: string[] }, resource: string): boolean => {
    return menuPermissions[resource] && menuPermissions[resource].length > 0;
  }
};
